import { cloneDeep } from 'lodash-es'
import { Vector3 } from 'three'
import {
  clonePoint,
  distanceBetweenPoints,
  getArcGripPoints,
  getArcParamsByThreePoints,
  getCircleQuadrantPoints,
  getEllipseAxes,
  getEllipseQuadrantPoints,
  projectPointToAxis,
} from '@/plugins/cad/module/utils/entityGripUtils'
import { SplineUtils } from '@/plugins/cad/module/utils/SplineUtils'
import { EntityType } from '../model/common/constant'
import type { IPoint } from '../model/common/interface'
import {
  type AlignDimensionEntity,
  type AngularDimensionEntity,
  type ArcEntity,
  type BaseEntity,
  type CircleEntity,
  type EllipseEntity,
  getAlignDimensionPoints,
  getAngularDimensionData,
  getDefaultMLeaderLandingDistance,
  getMLeaderElbowPoint,
  getMLeaderLandingDirection,
  getMLeaderLandingDistance,
  getMLeaderLandingEnd,
  getMLeaderLandingVector,
  getMLeaderTextGap,
  type HatchEntity,
  type IEntity,
  type MLeaderEntity,
  type MTextEntity,
  type PolylineEntity,
  type RayEntity,
  type SplineEntity,
  syncAngularDimensionTextPosition,
} from '../model/entity'

/**
 * 实体控制点（grip）描述。
 */
export interface EntityGrip {
  /** 控制点唯一标识 */
  id: string
  /** 所属实体 id */
  entityId: string
  /** 控制点种类（如 vertex、center、midpoint 等） */
  kind: string
  /** 控制点位置 */
  point: IPoint
  /** 附加元数据，供具体实体类型识别 */
  meta?: Record<string, any>
}

/**
 * 实体控制点处理器：按实体类型生成控制点、应用拖动与生成快照。
 */
export class EntityGripHandler {
  /**
   * 按实体类型生成控制点集合。
   *
   * @param entity - 目标实体
   * @returns 控制点列表，无支持类型时返回空数组
   */
  static getGripPoints(entity: IEntity): EntityGrip[] {
    switch (entity.type) {
      case EntityType.Polyline:
        return EntityGripHandler.getPolylineGrips(entity as PolylineEntity)
      case EntityType.Spline:
        return EntityGripHandler.getSplineGrips(entity as SplineEntity)
      case EntityType.Arc:
        return EntityGripHandler.getArcGrips(entity as ArcEntity)
      case EntityType.Circle:
        return EntityGripHandler.getCircleGrips(entity as CircleEntity)
      case EntityType.Ellipse:
        return EntityGripHandler.getEllipseGrips(entity as EllipseEntity)
      case EntityType.Ray:
        return EntityGripHandler.getRayGrips(entity as RayEntity)
      case EntityType.AlignDimension:
        return EntityGripHandler.getDimensionGrips(entity as AlignDimensionEntity)
      case EntityType.MLeader:
        return EntityGripHandler.getMLeaderGrips(entity as MLeaderEntity)
      case EntityType.AngularDimension:
        return EntityGripHandler.getAngularDimensionGrips(entity as AngularDimensionEntity)
      case EntityType.MText:
        return EntityGripHandler.getTextGrips(entity as MTextEntity)
      case EntityType.Hatch:
        return EntityGripHandler.getHatchGrips(entity as HatchEntity)
      default:
        return []
    }
  }

  /**
   * 将控制点移动到目标点并就地更新实体数据。
   *
   * @param entity - 目标实体
   * @param grip - 要拖动的控制点
   * @param targetPoint - 控制点的新位置
   */
  static applyGrip(entity: IEntity, grip: EntityGrip, targetPoint: IPoint): void {
    switch (entity.type) {
      case EntityType.Polyline:
        EntityGripHandler.applyPolylineGrip(entity as PolylineEntity, grip, targetPoint)
        break
      case EntityType.Spline:
        EntityGripHandler.applySplineGrip(entity as SplineEntity, grip, targetPoint)
        break
      case EntityType.Arc:
        EntityGripHandler.applyArcGrip(entity as ArcEntity, grip, targetPoint)
        break
      case EntityType.Circle:
        EntityGripHandler.applyCircleGrip(entity as CircleEntity, grip, targetPoint)
        break
      case EntityType.Ellipse:
        EntityGripHandler.applyEllipseGrip(entity as EllipseEntity, grip, targetPoint)
        break
      case EntityType.Ray:
        EntityGripHandler.applyRayGrip(entity as RayEntity, grip, targetPoint)
        break
      case EntityType.AlignDimension:
        EntityGripHandler.applyDimensionGrip(entity as AlignDimensionEntity, grip, targetPoint)
        break
      case EntityType.MLeader:
        EntityGripHandler.applyMLeaderGrip(entity as MLeaderEntity, grip, targetPoint)
        break
      case EntityType.AngularDimension:
        EntityGripHandler.applyAngularDimensionGrip(
          entity as AngularDimensionEntity,
          grip,
          targetPoint
        )
        break
      case EntityType.MText:
        EntityGripHandler.applyTextGrip(entity as MTextEntity, grip, targetPoint)
        break
      case EntityType.Hatch:
        EntityGripHandler.applyHatchGrip(entity as HatchEntity, grip, targetPoint)
        break
      default:
        break
    }
  }

  /**
   * 生成实体当前状态的深拷贝快照，用于在控制点编辑前保存初始数据。
   *
   * @param entity - 目标实体
   * @returns 序列化后的快照；实体未提供 serialize 时返回 null
   */
  static snapshot(entity: IEntity): Record<string, any> | null {
    const baseEntity = entity as BaseEntity
    if (typeof baseEntity.serialize !== 'function') {
      return null
    }
    return cloneDeep(baseEntity.serialize())
  }

  private static getPolylineGrips(entity: PolylineEntity): EntityGrip[] {
    const grips: EntityGrip[] = entity.vertices.map((vertex, index) => ({
      id: `${entity.id}:vertex:${index}`,
      entityId: entity.id,
      kind: 'vertex',
      point: { x: vertex.x, y: vertex.y },
      meta: { index },
    }))

    const segmentCount = entity.isClosed
      ? entity.vertices.length
      : Math.max(entity.vertices.length - 1, 0)

    for (let i = 0; i < segmentCount; i++) {
      const startIndex = i
      const endIndex = (i + 1) % entity.vertices.length
      const start = entity.vertices[startIndex]
      const end = entity.vertices[endIndex]
      grips.push({
        id: `${entity.id}:segmentMidpoint:${i}`,
        entityId: entity.id,
        kind: 'segmentMidpoint',
        point: {
          x: (start.x + end.x) / 2,
          y: (start.y + end.y) / 2,
        },
        meta: {
          startIndex,
          endIndex,
          angle: Math.atan2(end.y - start.y, end.x - start.x),
          scaleX: 1.2,
          scaleY: 0.75,
        },
      })
    }

    return grips
  }

  private static getSplineGrips(entity: SplineEntity): EntityGrip[] {
    const points =
      entity.method === 'FIT' && entity.fitPoints?.length ? entity.fitPoints : entity.controlPoints
    const kind = entity.method === 'FIT' ? 'fitPoint' : 'controlPoint'
    return (points || []).map((point, index) => ({
      id: `${entity.id}:${kind}:${index}`,
      entityId: entity.id,
      kind,
      point: clonePoint(point),
      meta: { index },
    }))
  }

  private static getArcGrips(entity: ArcEntity): EntityGrip[] {
    const { start, end, mid } = getArcGripPoints(entity)
    return [
      {
        id: `${entity.id}:center`,
        entityId: entity.id,
        kind: 'center',
        point: clonePoint(entity.center),
      },
      {
        id: `${entity.id}:start`,
        entityId: entity.id,
        kind: 'start',
        point: start,
      },
      {
        id: `${entity.id}:end`,
        entityId: entity.id,
        kind: 'end',
        point: end,
      },
      {
        id: `${entity.id}:mid`,
        entityId: entity.id,
        kind: 'mid',
        point: mid,
      },
    ]
  }

  private static getCircleGrips(entity: CircleEntity): EntityGrip[] {
    const quadrants = getCircleQuadrantPoints(entity)
    return [
      {
        id: `${entity.id}:center`,
        entityId: entity.id,
        kind: 'center',
        point: clonePoint(entity.center),
      },
      ...Object.entries(quadrants).map(([kind, point]) => ({
        id: `${entity.id}:${kind}`,
        entityId: entity.id,
        kind,
        point,
      })),
    ]
  }

  private static getEllipseGrips(entity: EllipseEntity): EntityGrip[] {
    const quadrants = getEllipseQuadrantPoints(entity)
    return [
      {
        id: `${entity.id}:center`,
        entityId: entity.id,
        kind: 'center',
        point: clonePoint(entity.center),
      },
      ...Object.entries(quadrants).map(([kind, point]) => ({
        id: `${entity.id}:${kind}`,
        entityId: entity.id,
        kind,
        point,
      })),
    ]
  }

  private static getRayGrips(entity: RayEntity): EntityGrip[] {
    return [
      {
        id: `${entity.id}:startPoint`,
        entityId: entity.id,
        kind: 'startPoint',
        point: clonePoint(entity.startPoint),
      },
    ]
  }

  private static getDimensionGrips(entity: AlignDimensionEntity): EntityGrip[] {
    const { d1, d2 } = getAlignDimensionPoints(
      new Vector3(entity.p1.x, entity.p1.y, 0),
      new Vector3(entity.p2.x, entity.p2.y, 0),
      new Vector3(entity.offsetPoint.x, entity.offsetPoint.y, 0)
    )
    return [
      { id: `${entity.id}:p1`, entityId: entity.id, kind: 'p1', point: clonePoint(entity.p1) },
      { id: `${entity.id}:p2`, entityId: entity.id, kind: 'p2', point: clonePoint(entity.p2) },
      {
        id: `${entity.id}:dimStart`,
        entityId: entity.id,
        kind: 'dimStart',
        point: { x: d1.x, y: d1.y },
      },
      {
        id: `${entity.id}:dimEnd`,
        entityId: entity.id,
        kind: 'dimEnd',
        point: { x: d2.x, y: d2.y },
      },
      {
        id: `${entity.id}:textPosition`,
        entityId: entity.id,
        kind: 'textPosition',
        point: clonePoint(entity.textPosition),
      },
    ]
  }

  private static getTextGrips(entity: MTextEntity): EntityGrip[] {
    return [
      {
        id: `${entity.id}:position`,
        entityId: entity.id,
        kind: 'position',
        point: clonePoint(entity.position),
      },
    ]
  }

  private static getMLeaderGrips(entity: MLeaderEntity): EntityGrip[] {
    const branch = entity.branches[0]
    const elbowPoint = getMLeaderElbowPoint(branch)
    const landingEnd = getMLeaderLandingEnd(branch)
    if (!branch || !elbowPoint || !landingEnd) {
      return []
    }
    return [
      {
        id: `${entity.id}:arrowPoint`,
        entityId: entity.id,
        kind: 'arrowPoint',
        point: clonePoint(branch.arrowPoint),
      },
      {
        id: `${entity.id}:elbowPoint`,
        entityId: entity.id,
        kind: 'elbowPoint',
        point: elbowPoint,
      },
      {
        id: `${entity.id}:textPosition`,
        entityId: entity.id,
        kind: 'textPosition',
        point: clonePoint(entity.textPosition || landingEnd),
      },
    ]
  }

  private static getAngularDimensionGrips(entity: AngularDimensionEntity): EntityGrip[] {
    return [
      {
        id: `${entity.id}:line1Start`,
        entityId: entity.id,
        kind: 'line1Start',
        point: clonePoint(entity.line1.startPoint),
      },
      {
        id: `${entity.id}:line1End`,
        entityId: entity.id,
        kind: 'line1End',
        point: clonePoint(entity.line1.endPoint),
      },
      {
        id: `${entity.id}:line2Start`,
        entityId: entity.id,
        kind: 'line2Start',
        point: clonePoint(entity.line2.startPoint),
      },
      {
        id: `${entity.id}:line2End`,
        entityId: entity.id,
        kind: 'line2End',
        point: clonePoint(entity.line2.endPoint),
      },
      {
        id: `${entity.id}:arcPoint`,
        entityId: entity.id,
        kind: 'arcPoint',
        point: clonePoint(entity.arcPoint),
      },
      {
        id: `${entity.id}:textPosition`,
        entityId: entity.id,
        kind: 'textPosition',
        point: clonePoint(entity.textPosition),
      },
    ]
  }

  private static getHatchGrips(entity: HatchEntity): EntityGrip[] {
    if (!entity.startPoint) {
      return []
    }
    return [
      {
        id: `${entity.id}:startPoint`,
        entityId: entity.id,
        kind: 'startPoint',
        point: clonePoint(entity.startPoint),
      },
    ]
  }

  private static applyPolylineGrip(entity: PolylineEntity, grip: EntityGrip, targetPoint: IPoint) {
    if (grip.kind === 'segmentMidpoint') {
      const startIndex = grip.meta?.startIndex
      const endIndex = grip.meta?.endIndex
      if (typeof startIndex !== 'number' || typeof endIndex !== 'number') {
        return
      }

      const start = entity.vertices[startIndex]
      const end = entity.vertices[endIndex]
      if (!start || !end) {
        return
      }

      const currentMidpoint = {
        x: (start.x + end.x) / 2,
        y: (start.y + end.y) / 2,
      }
      const dx = targetPoint.x - currentMidpoint.x
      const dy = targetPoint.y - currentMidpoint.y
      entity.vertices[startIndex] = { ...start, x: start.x + dx, y: start.y + dy }
      entity.vertices[endIndex] = { ...end, x: end.x + dx, y: end.y + dy }
      return
    }

    const index = grip.meta?.index
    if (typeof index !== 'number' || !entity.vertices[index]) {
      return
    }
    entity.vertices[index] = {
      ...entity.vertices[index],
      x: targetPoint.x,
      y: targetPoint.y,
    }
  }

  private static applySplineGrip(entity: SplineEntity, grip: EntityGrip, targetPoint: IPoint) {
    const index = grip.meta?.index
    if (typeof index !== 'number') {
      return
    }

    if (entity.method === 'FIT' && entity.fitPoints?.[index]) {
      entity.fitPoints[index] = clonePoint(targetPoint)
      if (entity.fitPoints.length >= 2) {
        const fitted = SplineUtils.fitPointsToSpline(
          entity.fitPoints.map(clonePoint),
          entity.degree
        )
        entity.controlPoints = fitted.controlPoints
        entity.knots = fitted.knots
        entity.degree = fitted.degree
      }
      return
    }

    if (entity.controlPoints?.[index]) {
      entity.controlPoints[index] = clonePoint(targetPoint)
    }
  }

  private static applyArcGrip(entity: ArcEntity, grip: EntityGrip, targetPoint: IPoint) {
    if (grip.kind === 'center') {
      entity.center = clonePoint(targetPoint)
      return
    }

    const { start, end, mid } = getArcGripPoints(entity)
    if (grip.kind === 'start') {
      const next = getArcParamsByThreePoints(targetPoint, mid, end)
      if (next) {
        entity.center = next.center
        entity.radius = next.radius
        entity.startAngle = next.startAngle
        entity.endAngle = next.endAngle
        entity.clockwise = next.clockwise
      }
      return
    }

    if (grip.kind === 'end') {
      const next = getArcParamsByThreePoints(start, mid, targetPoint)
      if (next) {
        entity.center = next.center
        entity.radius = next.radius
        entity.startAngle = next.startAngle
        entity.endAngle = next.endAngle
        entity.clockwise = next.clockwise
      }
      return
    }

    if (grip.kind === 'mid') {
      const next = getArcParamsByThreePoints(start, targetPoint, end)
      if (next) {
        entity.center = next.center
        entity.radius = next.radius
        entity.startAngle = next.startAngle
        entity.endAngle = next.endAngle
        entity.clockwise = next.clockwise
      }
    }
  }

  private static applyCircleGrip(entity: CircleEntity, grip: EntityGrip, targetPoint: IPoint) {
    if (grip.kind === 'center') {
      entity.center = clonePoint(targetPoint)
      return
    }
    entity.radius = Math.max(distanceBetweenPoints(entity.center, targetPoint), 1e-6)
  }

  private static applyEllipseGrip(entity: EllipseEntity, grip: EntityGrip, targetPoint: IPoint) {
    if (grip.kind === 'center') {
      entity.center = clonePoint(targetPoint)
      return
    }

    const { majorAxisUnit, minorAxisUnit, radiusX, radiusY } = getEllipseAxes(entity)

    if (grip.kind === 'right' || grip.kind === 'left') {
      const majorRadius = Math.max(
        Math.abs(projectPointToAxis(targetPoint, entity.center, majorAxisUnit)),
        1e-6
      )

      if (majorRadius < radiusY) {
        // 长轴被拉短到小于短轴时，交换长短轴；原短轴点保持不变。
        entity.majorAxisEndPoint = {
          x: minorAxisUnit.x * radiusY,
          y: minorAxisUnit.y * radiusY,
        }
        entity.axisRatio = majorRadius / radiusY
      } else {
        // 短轴点保持不变，仅更新长轴半径。
        entity.majorAxisEndPoint = {
          x: majorAxisUnit.x * majorRadius,
          y: majorAxisUnit.y * majorRadius,
        }
        entity.axisRatio = radiusY / majorRadius
      }
      return
    }

    if (grip.kind === 'top' || grip.kind === 'bottom') {
      const minorRadius = Math.max(
        Math.abs(projectPointToAxis(targetPoint, entity.center, minorAxisUnit)),
        1e-6
      )

      if (minorRadius > radiusX) {
        // 短轴被拉长到超过长轴时，交换长短轴；原长轴点保持不变。
        entity.majorAxisEndPoint = {
          x: minorAxisUnit.x * minorRadius,
          y: minorAxisUnit.y * minorRadius,
        }
        entity.axisRatio = radiusX / minorRadius
      } else {
        // 长轴点保持不变，仅更新短轴半径。
        entity.majorAxisEndPoint = {
          x: majorAxisUnit.x * radiusX,
          y: majorAxisUnit.y * radiusX,
        }
        entity.axisRatio = minorRadius / radiusX
      }
    }
  }

  private static applyRayGrip(entity: RayEntity, grip: EntityGrip, targetPoint: IPoint) {
    if (grip.kind === 'startPoint') {
      entity.startPoint = clonePoint(targetPoint)
    }
  }

  private static applyDimensionGrip(
    entity: AlignDimensionEntity,
    grip: EntityGrip,
    targetPoint: IPoint
  ) {
    if (grip.kind === 'p1') {
      entity.p1 = clonePoint(targetPoint)
      EntityGripHandler.syncDimensionTextPosition(entity)
    } else if (grip.kind === 'p2') {
      entity.p2 = clonePoint(targetPoint)
      EntityGripHandler.syncDimensionTextPosition(entity)
    } else if (grip.kind === 'dimStart' || grip.kind === 'dimEnd') {
      const { d1, d2 } = getAlignDimensionPoints(
        new Vector3(entity.p1.x, entity.p1.y, 0),
        new Vector3(entity.p2.x, entity.p2.y, 0),
        new Vector3(entity.offsetPoint.x, entity.offsetPoint.y, 0)
      )
      const sourcePoint = grip.kind === 'dimStart' ? { x: d1.x, y: d1.y } : { x: d2.x, y: d2.y }
      const dx = targetPoint.x - sourcePoint.x
      const dy = targetPoint.y - sourcePoint.y

      entity.offsetPoint = {
        x: entity.offsetPoint.x + dx,
        y: entity.offsetPoint.y + dy,
      }
      EntityGripHandler.syncDimensionTextPosition(entity, { d1, d2 })
    } else if (grip.kind === 'textPosition') {
      entity.textPosition = clonePoint(targetPoint)
      entity.isTextPositionEdited = true
    }
  }

  private static syncDimensionTextPosition(
    entity: AlignDimensionEntity,
    dimLine?: { d1: IPoint; d2: IPoint }
  ) {
    if (entity.isTextPositionEdited) {
      return
    }
    if (!dimLine) {
      dimLine = getAlignDimensionPoints(
        new Vector3(entity.p1.x, entity.p1.y, 0),
        new Vector3(entity.p2.x, entity.p2.y, 0),
        new Vector3(entity.offsetPoint.x, entity.offsetPoint.y, 0)
      )
    }
    const { d1, d2 } = dimLine
    entity.textPosition = {
      x: (d1.x + d2.x) / 2,
      y: (d1.y + d2.y) / 2,
    }
  }

  private static applyAngularDimensionGrip(
    entity: AngularDimensionEntity,
    grip: EntityGrip,
    targetPoint: IPoint
  ) {
    if (grip.kind === 'textPosition') {
      entity.textPosition = clonePoint(targetPoint)
      entity.isTextPositionEdited = true
      return
    }

    const originalData = {
      line1: {
        startPoint: clonePoint(entity.line1.startPoint),
        endPoint: clonePoint(entity.line1.endPoint),
      },
      line2: {
        startPoint: clonePoint(entity.line2.startPoint),
        endPoint: clonePoint(entity.line2.endPoint),
      },
      arcPoint: clonePoint(entity.arcPoint),
      textPosition: clonePoint(entity.textPosition),
      isTextPositionEdited: entity.isTextPositionEdited,
    }

    switch (grip.kind) {
      case 'line1Start':
        entity.line1.startPoint = clonePoint(targetPoint)
        break
      case 'line1End':
        entity.line1.endPoint = clonePoint(targetPoint)
        break
      case 'line2Start':
        entity.line2.startPoint = clonePoint(targetPoint)
        break
      case 'line2End':
        entity.line2.endPoint = clonePoint(targetPoint)
        break
      case 'arcPoint':
        entity.arcPoint = clonePoint(targetPoint)
        break
      default:
        return
    }

    syncAngularDimensionTextPosition(entity)
    const isValid = !!getAngularDimensionData(
      entity.line1,
      entity.line2,
      entity.arcPoint,
      entity.textPosition
    )

    if (!isValid) {
      entity.line1 = originalData.line1
      entity.line2 = originalData.line2
      entity.arcPoint = originalData.arcPoint
      entity.textPosition = originalData.textPosition
      entity.isTextPositionEdited = originalData.isTextPositionEdited
    }
  }

  private static applyTextGrip(entity: MTextEntity, grip: EntityGrip, targetPoint: IPoint) {
    if (grip.kind === 'position') {
      entity.position = clonePoint(targetPoint)
    }
  }

  private static applyMLeaderGrip(entity: MLeaderEntity, grip: EntityGrip, targetPoint: IPoint) {
    const branch = entity.branches[0]
    if (!branch) {
      return
    }
    const landingDirection = getMLeaderLandingDirection(branch, entity.textPosition)
    const landingVector = getMLeaderLandingVector(branch, landingDirection)
    const landingDistance = getMLeaderLandingDistance(
      branch,
      getDefaultMLeaderLandingDistance(entity.textHeight, entity.landingGap)
    )

    if (grip.kind === 'arrowPoint') {
      branch.arrowPoint = clonePoint(targetPoint)
      return
    }

    if (grip.kind === 'elbowPoint') {
      branch.vertices[0] = clonePoint(targetPoint)
      EntityGripHandler.syncMLeaderTextPosition(entity, {
        landingDirection,
        landingDistance,
        landingVector,
      })
      return
    }

    if (grip.kind === 'textPosition') {
      const elbowPoint = getMLeaderElbowPoint(branch)
      if (!elbowPoint) {
        return
      }
      const textDirection = targetPoint.x >= elbowPoint.x ? 1 : -1
      const textGap = getMLeaderTextGap(entity.textHeight, entity.landingGap)
      branch.vertices[0] = {
        x: elbowPoint.x,
        y: targetPoint.y,
      }
      branch.vertices[1] = {
        x: targetPoint.x - textDirection * textGap,
        y: targetPoint.y,
      }
      entity.textPosition = clonePoint(targetPoint)
      entity.isTextPositionEdited = true
    }
  }

  private static syncMLeaderTextPosition(
    entity: MLeaderEntity,
    options?: { landingDirection?: 1 | -1; landingDistance?: number; landingVector?: IPoint }
  ) {
    const branch = entity.branches[0]
    const elbowPoint = getMLeaderElbowPoint(branch)
    if (!branch || !elbowPoint) {
      return
    }
    const landingDistance =
      options?.landingDistance ??
      getMLeaderLandingDistance(
        branch,
        getDefaultMLeaderLandingDistance(entity.textHeight, entity.landingGap)
      )
    const textGap = getMLeaderTextGap(entity.textHeight, entity.landingGap)
    const landingVector =
      options?.landingVector ??
      getMLeaderLandingVector(
        branch,
        options?.landingDirection ?? getMLeaderLandingDirection(branch, entity.textPosition)
      )
    const landingEnd =
      entity.isTextPositionEdited && entity.textPosition
        ? {
            x: entity.textPosition.x - landingVector.x * textGap,
            y: entity.textPosition.y - landingVector.y * textGap,
          }
        : {
            x: elbowPoint.x + landingVector.x * landingDistance,
            y: elbowPoint.y + landingVector.y * landingDistance,
          }
    branch.vertices[0] = clonePoint(elbowPoint)
    branch.vertices[1] = clonePoint(landingEnd)
    if (!entity.isTextPositionEdited) {
      entity.textPosition = {
        x: landingEnd.x + landingVector.x * textGap,
        y: landingEnd.y + landingVector.y * textGap,
      }
    }
  }

  private static applyHatchGrip(entity: HatchEntity, grip: EntityGrip, targetPoint: IPoint) {
    if (grip.kind === 'startPoint') {
      entity.startPoint = clonePoint(targetPoint)
    }
  }
}
